
<script setup>
import { ref, watch, computed, onMounted, onBeforeUnmount } from 'vue'

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  options: {
    type: Array,
    required: true,
  },
  label: {
    type: String,
    default: 'Sélectionner',
  },
  placeholder: {
    type: String,
    default: 'Choisissez une ou plusieurs options',
  },
})

const emit = defineEmits(['update:modelValue'])

const isOpen = ref(false)

const selectedOptions = computed(() =>
  props.options.filter(opt => props.modelValue.includes(opt.key))
)

const toggleDropdown = () => {
  isOpen.value = !isOpen.value
}

const toggleOption = option => {
  const current = [...props.modelValue]
  const index = current.indexOf(option.key)
  if (index === -1) {
    current.push(option.key)
  } else {
    current.splice(index, 1)
  }
  emit('update:modelValue', current)
}

const removeOption = option => {
  const current = props.modelValue.filter(val => val !== option.key)
  emit('update:modelValue', current)
}

const isSelected = option => props.modelValue.includes(option.key)

// Click outside → ferme le dropdown. Listener nommé pour pouvoir
// removeEventListener au unmount (sinon leak mémoire à chaque
// démount/remount de la liste, et cumul de handlers chaque fois
// qu'un MultiSelectTag est mounté).
const onDocClickClose = e => {
  if (!e.target.closest('.relative')) {
    isOpen.value = false
  }
}
onMounted(() => document.addEventListener('click', onDocClickClose))
onBeforeUnmount(() => document.removeEventListener('click', onDocClickClose))
</script>

<template>
  <div class="w-full">

    <label class="block mb-1">{{ label }}</label>

    <div
      class="relative flex flex-row items-center w-full my-2 bg-transparent border rounded-lg shadow-sm ppx-4 ppy-2 border-secondary focus-within:ring-2 focus-within:ring-blue-500"
      @click="toggleDropdown"
    >
      <div class="flex flex-wrap gap-1 p-2 min-h-[40px] cursor-pointer items-center">
        <span
          v-if="!selectedOptions.length"
          class="px-2 text-gray-500"
        >{{ placeholder }}</span>

        <span
          v-for="option in selectedOptions"
          :key="option.key"
          class="inline-flex items-center justify-center px-2 py-1 text-sm text-gray-800 rounded bg-accent"
        >
          {{ option.value }}
          <button
            @click.stop="removeOption(option)"
            class="ml-1 text-xs text-red-600 hover:text-red-800"
          >
            ✕
          </button>
        </span>
      </div>

      <div
        v-if="isOpen"
        class="absolute left-0 px-2 py-2 top-12 z-20 w-full mt-1 max-h-60 overflow-y-auto rounded-lg shadow-lg bg-secondary custumScroll space-y-1"
      >
        <div
          v-for="option in options"
          :key="option.key"
          @click.stop="toggleOption(option)"
          class="flex items-center px-4 py-2 rounded-lg cursor-pointer hover:bg-accent hover:text-gray-800"
          :class="{ 'bg-accent-100 font-medium': isSelected(option) }"
        >
          {{ option.value }}
        </div>
      </div>
    </div>
  </div>
</template>
